import { CEDE_CEXS, getCexLabel, getCexLogo } from "@cede/utils";
import type { LucideIcon } from "lucide-react";
import { ArrowRight, TrendingUp, Users, Zap, Shield } from "lucide-react";

export type SelfRegisterExchangeOption = {
  value: string;
  label: string;
  logo?: string;
  category: "cex" | "perps" | "dex" | "custom";
  isCustom?: boolean;
};

export const MIN_CAMPAIGN_DAYS = 7;
// Prize pool rates based on volume-to-reward ratios from campaign data
// Ratio 500 = $500 volume needed per $1 reward (conservative, typical campaigns)
// Ratio 170 = $170 volume needed per $1 reward (generous, higher prize pool estimate)
export const PRIZE_POOL_LOW_RATE = 0.002;
export const PRIZE_POOL_HIGH_RATE = 0.006;

export const BD_TELEGRAM_LINK = "https://t.me/GabrielBegazo";
export const BD_EMAIL = "gabriel.begazo@cedelabs.io";

/** Prefix for custom exchange values (e.g., "custom:My Exchange") */
export const CUSTOM_EXCHANGE_PREFIX = "custom:";

const CEX_EXCHANGES = [
  CEDE_CEXS.BINANCE,
  CEDE_CEXS.BYBIT,
  CEDE_CEXS.KUCOIN,
  CEDE_CEXS.CRYPTOCOM,
  CEDE_CEXS.GATEIO,
  CEDE_CEXS.OKX,
  CEDE_CEXS.BITGET,
  CEDE_CEXS.MEXC,
  CEDE_CEXS.HTX,
  CEDE_CEXS.KRAKEN,
  CEDE_CEXS.COINBASE,
  CEDE_CEXS.BITFINEX,
  CEDE_CEXS.BITSTAMP,
  CEDE_CEXS.BITMART,
  CEDE_CEXS.BITHUMB,
  CEDE_CEXS.LBANK,
] as const;

/** Set of static exchange values (CEX and perps) - used to identify DEX selections */
export const STATIC_EXCHANGE_VALUES = new Set([
  ...CEX_EXCHANGES,
  CEDE_CEXS.HYPERLIQUID,
  "bybit-perps",
  "dydx",
  "lighter",
  CEDE_CEXS.KRAKEN_FUTURES,
]);

/** Check if any exchange is a custom exchange (user-entered) */
export const hasCustomExchange = (exchanges: string[]): boolean => {
  return exchanges.some((exchange) => exchange.startsWith(CUSTOM_EXCHANGE_PREFIX));
};

/** Check if any exchange is a DEX (from Mobula API, not in static options, not custom) */
export const hasDexExchange = (exchanges: string[]): boolean => {
  return exchanges.some(
    (exchange) => !exchange.startsWith(CUSTOM_EXCHANGE_PREFIX) && !STATIC_EXCHANGE_VALUES.has(exchange),
  );
};

/**
 * Check if chains field should be displayed.
 * Returns true if any DEX or custom exchange is selected.
 */
export const shouldShowChainsField = (exchanges: string[]): boolean => {
  return hasDexExchange(exchanges) || hasCustomExchange(exchanges);
};

/**
 * Check if chains field is required (not just shown).
 * Returns true only if a DEX is selected (custom exchanges make chains optional).
 */
export const chainsAreRequired = (exchanges: string[]): boolean => {
  return hasDexExchange(exchanges);
};

// Static CEX and perps options with logos - DEX options come from Mobula API
export const SELF_REGISTER_EXCHANGE_OPTIONS: SelfRegisterExchangeOption[] = [
  ...CEX_EXCHANGES.map((exchangeId) => ({
    value: exchangeId,
    label: getCexLabel(exchangeId),
    logo: getCexLogo(exchangeId),
    category: "cex" as const,
  })),
  { value: CEDE_CEXS.HYPERLIQUID, label: "Hyperliquid", logo: getCexLogo(CEDE_CEXS.HYPERLIQUID), category: "perps" },
  { value: "bybit-perps", label: "Bybit Perps", logo: getCexLogo(CEDE_CEXS.BYBIT), category: "perps" },
  { value: "dydx", label: "dYdX", category: "perps" },
  { value: "lighter", label: "Lighter", category: "perps" },
  {
    value: CEDE_CEXS.KRAKEN_FUTURES,
    label: "Kraken Futures",
    logo: getCexLogo(CEDE_CEXS.KRAKEN_FUTURES),
    category: "perps",
  },
];

export const SELF_REGISTER_DISCOVERY_OPTIONS = [
  { value: "twitter", label: "Twitter / X" },
  { value: "telegram", label: "Telegram" },
  { value: "friend", label: "Friend" },
  { value: "referral", label: "Referral" },
  { value: "vc", label: "VC / Investor" },
  { value: "exchange", label: "Exchange" },
  { value: "google", label: "Google / Search" },
  { value: "other", label: "Other" },
];

/** Landing page content configuration - edit copy here without touching layout */
export const SELF_REGISTER_LANDING_CONFIG = {
  headline: {
    line1: "Run your own trading campaign",
    line2: "on",
  },
  subtitle:
    "Ready to boost your token's visibility and trading activity? Launch a trading competition that rewards real traders and drives organic volume to your markets.",
  cta: {
    text: "Launch Your Campaign",
    icon: ArrowRight,
  },
  benefits: [
    {
      icon: TrendingUp,
      title: "Drive Organic Volume",
      description: "Generate real trading activity with proven incentive structures",
    },
    {
      icon: Users,
      title: "Acquire New Traders",
      description: "Tap into our active community of verified traders",
    },
    {
      icon: Zap,
      title: "Quick Launch",
      description: "From setup to live campaign in hours",
    },
    {
      icon: Shield,
      title: "Anti-Wash Trading",
      description: "Advanced detection ensures only genuine trading activity counts",
    },
  ] as Array<{ icon: LucideIcon; title: string; description: string }>,
};
